import {
  Card,
  CardCover,
  CardBody,
  CardTag,
  CardTitle,
  CardDescription,
  CardMeta,
  CardStats,
  CardFooter,
  CardActions,
  CardSkeleton,
  Button,
} from "@aisquare/ui";
import { Section, VariantBlock } from "../lib/Section";

/**
 * Verification harness for the Card surface. Card is ROUND by default and wears
 * the Onyx hex-notch ONLY when it's clickable (`interactive` / `onClick`) — the
 * notch is a press affordance for clickable cards, while static display cards
 * stay simple + round. On the clickable (notched) card: the `nx-s` notch traces
 * the top-right chamfer, the `.elev-card` drop-shadow survives the clip (no
 * slice) and deepens to `.elev-panel` on hover, and the continuous `nx-rim`
 * keyline rings the full silhouette (a real border would be sliced at the notch).
 */

// Inline SVG data URI cover so the demo never depends on the network.
const COVER =
  "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='400' height='225'%3E%3Crect width='400' height='225' fill='%230A2540'/%3E%3C/svg%3E";

export function CardDemo() {
  return (
    <Section
      id="card"
      title="Card"
      description="The slot-composed entity-card surface (Studios, Experiences, Collections). It's round by default and wears the Onyx hex-notch (nx-s) ONLY when clickable — pass onClick / interactive and it becomes a notched, pressable card with a clip-safe .elev-card drop-shadow (→ .elev-panel on hover) and a continuous nx-rim keyline ring; a static display card stays simple + round (real border + soft shadow). Two layout modes via view (grid | list); hover is opt-in (lift | glow | none)."
    >
      <VariantBlock
        label="Clickable card — notched (the offset-key affordance)"
        caption="onClick / interactive → the nx-s notch + clip-safe drop-shadow + nx-rim keyline; cover clips to the notch top edge"
      >
        <Card motionEnabled={false} onClick={() => {}} className="max-w-sm">
          <CardCover src={COVER} alt="" aspect="16/9" hoverZoom={false} />
          <CardBody>
            <CardTag tone="violet">Quest</CardTag>
            <CardTitle>Build your first AI Studio</CardTitle>
            <CardDescription>
              Walk through topic, content, and AI-experience setup in one guided flow.
            </CardDescription>
            <CardMeta items={["Aug 6, 2025", "Sahaj Garg", "2 min read"]} />
            <CardStats stats={[{ value: "42 enrolled" }, { value: "4.8 ★" }]} />
          </CardBody>
          <CardFooter>
            <CardActions>
              <Button size="sm">Open</Button>
            </CardActions>
          </CardFooter>
        </Card>
      </VariantBlock>

      <VariantBlock
        label="Static / display card — simple + round"
        caption="no onClick → stays a plain rounded surface (a real border + soft shadow), not notched"
      >
        <Card motionEnabled={false} hover="none" className="max-w-sm">
          <CardCover src={COVER} alt="" aspect="16/9" hoverZoom={false} />
          <CardBody>
            <CardTitle>This week in your studios</CardTitle>
            <CardDescription>
              A read-only summary surface — nothing to click, so it stays simple and round.
            </CardDescription>
            <CardMeta items={["Newsletter", "6 min read"]} />
          </CardBody>
        </Card>
      </VariantBlock>

      <VariantBlock
        label="Clickable list card — notched"
        caption="horizontal row; same notched silhouette + keyline, inner thumbnail keeps a small radius"
      >
        <Card motionEnabled={false} view="list" onClick={() => {}} className="max-w-xl">
          <CardCover src={COVER} alt="" view="list" aspect="4/3" hoverZoom={false} />
          <CardBody view="list">
            <CardTitle size="md">Weekly AI digest</CardTitle>
            <CardDescription clamp={2}>
              The five most important things that shipped across your studios this week.
            </CardDescription>
            <CardMeta items={["Newsletter", "6 min read"]} />
          </CardBody>
        </Card>
      </VariantBlock>

      <VariantBlock
        label="Hover (clickable) — lift · glow"
        caption="lift deepens the clip-safe drop-shadow; glow tints the nx-rim keyline primary on hover"
      >
        <Card motionEnabled={false} hover="lift" onClick={() => {}} className="max-w-xs">
          <CardCover src={COVER} alt="" aspect="16/9" hoverZoom={false} />
          <CardBody>
            <CardTitle size="sm">Hover to lift</CardTitle>
            <CardDescription clamp={2}>
              The whole surface is the target; the drop-shadow deepens.
            </CardDescription>
          </CardBody>
        </Card>
        <Card motionEnabled={false} hover="glow" onClick={() => {}} className="max-w-xs">
          <CardCover src={COVER} alt="" aspect="16/9" hoverZoom={false} />
          <CardBody>
            <CardTitle size="sm">Hover to glow</CardTitle>
            <CardDescription clamp={2}>
              The keyline ring picks up the primary tint on hover.
            </CardDescription>
          </CardBody>
        </Card>
      </VariantBlock>

      <VariantBlock
        label="Loading skeleton"
        caption="mirrors a clickable card's notched silhouette while a result set loads"
      >
        <CardSkeleton view="grid" className="max-w-sm" />
      </VariantBlock>
    </Section>
  );
}
